/**
 * Maps a campaign's folder / line-item / creative tree into the BLS picker model.
 *
 * Parent flight = creative.folder — the experiment bucket users are assigned to.
 * Flight (set)  = line item, or a standalone creative when there is no line item
 *                 (which creatives from that parent to include).
 *
 * Size matching is between parent flights: every included ad needs the same
 * size on every other selected parent. Duplicates of a size inside one parent
 * are allowed. One unmatched size is a size mismatch and blocks Continue.
 *
 * API_TODO: create/update still indexes creative_groups as folder names.
 * Selected set IDs stay in wizard extras; the payload sends unique parent folders.
 */

import { ELIGIBILITY, MEDIA_TYPE } from './constants.js';

const VIDEO_TYPES = ['VAST', 'VPAID', 'Video'];

export function mediaTypeForCreative(creative) {
  const type = creative?.type;
  if (type === 1 || type === 2 || type === '1' || type === '2') return MEDIA_TYPE.VIDEO;
  const label = String(type || '').toUpperCase();
  if (VIDEO_TYPES.some((token) => label.includes(token))) return MEDIA_TYPE.VIDEO;
  return MEDIA_TYPE.DISPLAY;
}

export function isDisplayCreative(creative) {
  return mediaTypeForCreative(creative) === MEDIA_TYPE.DISPLAY;
}

export function sizeLabelForCreative(creative) {
  const width = Number(creative?.width) || 0;
  const height = Number(creative?.height) || 0;
  if (width && height) return `${width}×${height}`;
  return creative?.type || 'Unknown size';
}

export function folderNameForCreative(creative) {
  return creative?.folder || 'No folder';
}

function mapCreativeRow(creative) {
  const liveIds = (creative?.liveCreatives || []).map((live) => live.id).filter(Boolean);
  let eligibility = ELIGIBILITY.ELIGIBLE;
  if (!creative?.id) eligibility = ELIGIBILITY.MISSING_ID;
  else if (!liveIds.length) eligibility = ELIGIBILITY.INACTIVE;

  return {
    id: creative.id,
    name: creative.name || creative.id,
    width: Number(creative.width) || 0,
    height: Number(creative.height) || 0,
    type: creative.type || 'Banner',
    sizeLabel: sizeLabelForCreative(creative),
    mediaType: mediaTypeForCreative(creative),
    liveIds,
    eligibility,
  };
}

/**
 * @param {object} campaign
 * @returns {{ parents: object[], sets: object[] }}
 */
export function buildFlightHierarchy(campaign) {
  const creatives = campaign?.creatives || [];
  const lineItems = campaign?.lineItems || [];
  const folders = {};

  creatives.forEach((creative) => {
    const folder = folderNameForCreative(creative);
    if (!folders[folder]) folders[folder] = [];
    folders[folder].push(creative);
  });

  const parents = [];
  const sets = [];

  Object.keys(folders)
    .sort((a, b) => a.localeCompare(b))
    .forEach((folderName) => {
      const folderCreatives = folders[folderName];
      const processedLineItemIds = new Set();
      const parentSets = [];

      folderCreatives.forEach((creative) => {
        if (creative.lineItemID) {
          if (processedLineItemIds.has(creative.lineItemID)) return;
          processedLineItemIds.add(creative.lineItemID);
          const lineItem =
            lineItems.find((item) => item.id === creative.lineItemID) || {
              id: creative.lineItemID,
              name: 'Creative set',
            };
          const setCreatives = folderCreatives
            .filter((row) => row.lineItemID === creative.lineItemID)
            .map(mapCreativeRow);
          if (!setCreatives.length) return;
          parentSets.push(makeSet(`set-${lineItem.id}`, lineItem.name, folderName, setCreatives));
        } else {
          const row = mapCreativeRow(creative);
          parentSets.push(makeSet(`creative-${creative.id}`, row.name, folderName, [row]));
        }
      });

      if (!parentSets.length) return;

      const mediaTypes = [...new Set(parentSets.map((set) => set.mediaType))];
      parents.push({
        id: folderName,
        name: folderName,
        setCount: parentSets.length,
        adCount: parentSets.reduce((sum, set) => sum + set.creatives.length, 0),
        mediaType: mediaTypes.length === 1 ? mediaTypes[0] : MEDIA_TYPE.DISPLAY,
      });
      sets.push(...parentSets);
    });

  return { parents, sets };
}

function makeSet(id, name, parentId, creatives) {
  const mediaTypes = [...new Set(creatives.map((row) => row.mediaType))];
  const eligibility = creatives.some((row) => row.eligibility === ELIGIBILITY.MISSING_ID)
    ? ELIGIBILITY.MISSING_ID
    : creatives.every((row) => row.eligibility === ELIGIBILITY.INACTIVE)
      ? ELIGIBILITY.INACTIVE
      : ELIGIBILITY.ELIGIBLE;

  return {
    id,
    name,
    parentId,
    mediaType: mediaTypes.length === 1 ? mediaTypes[0] : mediaTypes[0] || MEDIA_TYPE.DISPLAY,
    mixedMedia: mediaTypes.length > 1,
    creatives,
    eligibility,
  };
}

export function uniqueParentIdsForSets(sets, selectedSetIds) {
  const ids = selectedSetIds || [];
  const selected = sets.filter((set) => ids.includes(set.id));
  return [...new Set(selected.map((set) => set.parentId))];
}

export function setIdsForParentIds(sets, parentIds) {
  const parents = new Set((parentIds || []).map(String));
  return (sets || [])
    .filter((set) => parents.has(String(set.parentId)))
    .map((set) => set.id);
}

function uniqueSizeLabelsFromCreatives(creatives) {
  const sizes = [];
  (creatives || []).forEach((row) => {
    const label = row?.sizeLabel;
    if (label && !sizes.includes(label)) sizes.push(label);
  });
  return sizes;
}

/** Collapse selected Flights into parent-flight size bags. */
export function groupSelectedByParent(sets) {
  const groups = new Map();
  (sets || []).forEach((set) => {
    const parentId = set.parentId != null && String(set.parentId) !== '' ? String(set.parentId) : String(set.id);
    if (!groups.has(parentId)) {
      groups.set(parentId, {
        id: parentId,
        parentId,
        name: parentId,
        creatives: [],
        sets: [],
      });
    }
    const group = groups.get(parentId);
    group.sets.push(set);
    group.creatives.push(...(set.creatives || []));
  });
  return [...groups.values()];
}

export function sizeIntersection(sets) {
  if (!sets.length) return [];
  const sizeSets = sets.map((set) => uniqueSizeLabelsFromCreatives(set.creatives));
  if (!sizeSets[0].length) return [];
  return sizeSets[0].filter((size) => sizeSets.every((list) => list.includes(size)));
}

/** Unique sizes that can be compared across selected parent flights. */
export function matchingSizesForParents(sets) {
  const parents = groupSelectedByParent(sets);
  if (!parents.length) return [];
  if (parents.length === 1) return uniqueSizeLabelsFromCreatives(parents[0].creatives);
  return sizeIntersection(parents);
}

/**
 * Hard mismatch when two or more parent flights have any unmatched ad size,
 * including a single extra size on one parent.
 */
export function hasSizeMismatch(sets) {
  const parents = groupSelectedByParent(sets);
  if (parents.length <= 1) return false;
  const matching = new Set(sizeIntersection(parents));
  if (!matching.size) return true;
  return (sets || []).some((set) =>
    (set.creatives || []).some((row) => row.sizeLabel && !matching.has(row.sizeLabel))
  );
}

export function isMatchingSize(sizeLabel, selectedSets) {
  const parents = groupSelectedByParent(selectedSets);
  if (parents.length <= 1) return true;
  return matchingSizesForParents(selectedSets).includes(sizeLabel);
}

export function isDisplaySet(set) {
  if (!set) return false;
  if (set.mediaType && set.mediaType !== MEDIA_TYPE.DISPLAY) return false;
  if (set.mixedMedia) return false;
  return !(set.creatives || []).some((row) => row.mediaType && row.mediaType !== MEDIA_TYPE.DISPLAY);
}

export function hasNonDisplayMedia(sets) {
  return (sets || []).some((set) => !isDisplaySet(set));
}

export function hasMixedMedia(sets) {
  return new Set((sets || []).map((set) => set.mediaType)).size > 1;
}

export function equalSplitPercent(count) {
  if (!count) return 0;
  return Math.floor(100 / count);
}

/** Sizes on this Flight that have no counterpart on every other parent flight. */
export function missingSizesForSet(set, selectedSets) {
  if (!set) return [];
  const already = (selectedSets || []).some((item) => item.id === set.id);
  const context = already ? selectedSets : [...(selectedSets || []), set];
  const parents = groupSelectedByParent(context);
  if (parents.length <= 1) return [];
  const matching = new Set(sizeIntersection(parents));
  return uniqueSizeLabelsFromCreatives(set.creatives).filter((size) => !matching.has(size));
}

export function setHasMatchingSize(set, selectedSets) {
  if (!set) return false;
  const already = (selectedSets || []).some((item) => item.id === set.id);
  const context = already ? selectedSets : [...(selectedSets || []), set];
  const parents = groupSelectedByParent(context);
  if (parents.length <= 1) return true;
  const matching = new Set(sizeIntersection(parents));
  return (set.creatives || []).some((row) => matching.has(row.sizeLabel));
}

export function matchingCreativeCount(sets) {
  const matching = new Set(matchingSizesForParents(sets));
  const parents = groupSelectedByParent(sets);
  if (parents.length <= 1) {
    return (sets || []).reduce((sum, set) => sum + (set.creatives?.length || 0), 0);
  }
  return (sets || []).reduce(
    (sum, set) => sum + (set.creatives || []).filter((row) => matching.has(row.sizeLabel)).length,
    0
  );
}

function isUnpublishedCampaignCreative(creative) {
  return Boolean(creative?.id) && !(creative.liveCreatives || []).length;
}

/**
 * Unpublished campaign creatives in the selected BLS flights.
 * begin() writes their live IDs into Cloudflare KV.
 */
export function unpublishedCreativesForLaunch(campaign, selectedSets) {
  if (!selectedSets?.length) return [];
  const byId = new Map((campaign?.creatives || []).map((creative) => [creative.id, creative]));
  const seen = new Set();
  const rows = [];

  selectedSets.forEach((set) => {
    (set.creatives || []).forEach((row) => {
      const creative = byId.get(row.id);
      if (!isUnpublishedCampaignCreative(creative) || seen.has(creative.id)) return;
      seen.add(creative.id);
      rows.push({
        id: creative.id,
        name: creative.name || creative.id,
        setName: set.name || '',
        creative,
      });
    });
  });

  return rows;
}
